'use client';
/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Canvas } from '@react-three/fiber';
import { Environment, OrbitControls, Sparkles } from '@react-three/drei';
import { VoiceAgentVisualization } from './voice-agent-visualization';
import { VoiceControls } from './voice-controls';

interface MiniVoiceAgentDialogProps {
	onMaximize: () => void;
	onVolumeChange: (volume: number) => void;
	onSpeakingStateChange: (isSpeaking: boolean) => void;
	isAssistantSpeaking: boolean;
	isConnected: boolean;
	setIsConnected: (connected: boolean) => void;
	userData?: {
		email?: string;
		podIds?: string[];
		name?: string;
		[key: string]: unknown;
	};
	isOpen: boolean;
	onClose: () => void;
	volumeLevel: number;
}

export function MiniVoiceAgentDialog({
	onMaximize,
	onVolumeChange,
	onSpeakingStateChange,
	isAssistantSpeaking,
	isConnected,
	setIsConnected,
	userData,
	isOpen,
	onClose,
	volumeLevel,
}: MiniVoiceAgentDialogProps) {
	// Add logs to track component lifecycle and state changes
	React.useEffect(() => {
		console.log('🔍 [MiniVoiceAgentDialog] Render state:', {
			isOpen,
			isConnected,
			shouldRender: isOpen && isConnected,
			timestamp: new Date().toISOString(),
		});
	}, [isOpen, isConnected]);

	// Track when dialog is clicked to maximize
	const handleMaximizeClick = React.useCallback(() => {
		console.log('🔍 [MiniVoiceAgentDialog] Maximize clicked', {
			isOpen,
			isConnected,
			timestamp: new Date().toISOString(),
		});
		onMaximize();
	}, [onMaximize, isOpen, isConnected]);

	return (
		<AnimatePresence
			onExitComplete={() => {
				console.log(
					'🔍 [MiniVoiceAgentDialog] Exit animation completed',
					{
						timestamp: new Date().toISOString(),
					}
				);
			}}
		>
			{isOpen && isConnected && (
				<motion.div
					initial={{ opacity: 0, scale: 0.95, y: 20 }}
					animate={{ opacity: 1, scale: 1, y: 0 }}
					exit={{ opacity: 0, scale: 0.95, y: 20 }}
					transition={{ duration: 0.2 }}
					className='fixed bottom-2 right-2 z-[202] w-[200px] h-[200px] rounded-xl overflow-hidden cursor-pointer'
					onClick={handleMaximizeClick}
					onAnimationStart={() => {
						console.log(
							'🔍 [MiniVoiceAgentDialog] Animation started'
						);
					}}
				>
					{/* Canvas Container */}
					<div className='absolute inset-0'>
						<Canvas
							camera={{
								position: [0, 0, 15],
								fov: 45,
							}}
						>
							<React.Suspense fallback={null}>
								<Environment
									files='/assets/dikhololo_night_1k.hdr'
									background={false}
									blur={0.5 + volumeLevel * 0.6}
								/>
								<Sparkles
									count={22}
									scale={8}
									size={8}
									speed={0.3}
									color='#ffd700'
								/>
								<VoiceAgentVisualization
									volumeLevel={volumeLevel}
									isAssistantSpeaking={isAssistantSpeaking}
								/>
								<OrbitControls
									enableZoom={false}
									autoRotate
									autoRotateSpeed={1}
									target={[0, 0, 0]}
								/>
							</React.Suspense>
						</Canvas>
					</div>

					{/* Voice Controls */}
					<div className='absolute bottom-2 left-0 right-0'>
						<VoiceControls
							onVolumeChange={onVolumeChange}
							onSpeakingStateChange={onSpeakingStateChange}
							onOpenChange={(isOpen) => {
								console.log(
									'🔍 [MiniVoiceAgentDialog] VoiceControls connection changed:',
									{
										newConnectionState: isOpen,
										previouslyConnected: isConnected,
										timestamp: new Date().toISOString(),
									}
								);
								setIsConnected(isOpen);
							}}
							userData={userData}
							isMinimized={true}
						/>
					</div>
				</motion.div>
			)}
		</AnimatePresence>
	);
}
